import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  QModal,
  QModalActions,
  QModalBody,
  QModalProps,
} from '../../QComponents';
import { QBox, QFlex, QStack } from '../../QLayouts';
import { QButton, QInput } from '../../QAtoms';
import { DataProps, usePartitionedChildren } from '../../utils';
import { useCurrentUser } from '../../hooks';
import { useDataProvider } from '../Table/DataProviders';
import { DataView } from './View';
import { QSelectableList, QSelectableListProps } from './SelectableList';
import { DatumPredicate, Selectable } from './types';
import {
  ConditionalErrorAlert,
  dropUnselectedAndMergeNew,
  getSelected,
  toMap,
  toggleAllDirty,
} from './SelectHelpers';
import { QuickSearchFilters } from './QuickSearch';
import { SearchResource } from '../../types/domains/Search';
export type MultiSelectProps<T> = Pick<QModalProps, 'isOpen'> &
  Pick<QSelectableListProps<T>, 'defaultSortBy'> &
  DataProps & {
    /**
     * These accessors are a type-agnostic way to uniquely identify items.
     *
     * TODO: Simplfy this to just `id` in a breaking change, do away with
     * the object wrapping.
     */
    accessors: Pick<QSelectableListProps<T>['accessors'], 'id'>;
    /**
     * Invoked when the user wishes to finalise their selection.
     *
     * This component will enter an in-progress state until this promise
     * settles.
     * This component doesn't care whether the promise resolves or rejects.
     *
     * @param selection - An array of the selected items only.
     * The selected items may include items that are no longer
     * available in the data provider.
     */
    onSelect: (selection: readonly T[]) => Promise<unknown>;
    /**
     * Invoked when the user wishes to cancel the selection process.
     */
    onCancel: () => void;
    /**
     * Optionally provide a function to determine if an item should be pre-selected.
     */
    isItemPreSelected?: (item: T) => boolean;
    /**
     * Optionally provide a function to determine if an item should be disabled.
     */
    isItemDisabled?: (item: T) => boolean;
    /**
     * The minimum number of selections required to enable the confirmation button.
     */
    requiredSelections?: number;
    /**
     * Defines which fields to display, as well as how to display them.
     */
    view: DataView<T>;
    /**
     * What action is going to happen when the user confirms their selection?
     * e.g. "Confirm", "Assign", etc
     */
    action?: string;
    /**
     * Will hide checkboxes and actionable buttons if true.
     */
    isReadOnly?: boolean;
    searchPlaceholder?: string;
    size?: Extract<QModalProps['size'], '4xl' | '6xl'>;
    children?: React.ReactNode;
    /**
     * Flag to determine if table should pin selected rows to top of table
     */
    shouldPin?: boolean;
    displayFilters?: boolean;
  };

export function MultiSelect<T>({
  accessors,
  onSelect,
  onCancel,
  isItemPreSelected,
  isItemDisabled,
  requiredSelections = 0,
  view,
  searchPlaceholder,
  defaultSortBy,
  action = 'Confirm',
  size = '4xl',
  isOpen,
  isReadOnly,
  children,
  shouldPin = false,
  displayFilters = false,
  ...dataProps
}: Readonly<MultiSelectProps<T>>): React.ReactElement {
  const {
    data,
    onSearchTermChange,
    isLoading,
    isFetchingNextPage,
    error,
    fetchNextPage,
    hasNextPage,
    search,
  } = useDataProvider<T>();

  const { companyId } = useCurrentUser();
  const [selections, setSelections] = useState<readonly Selectable<T>[]>([]);
  const [currentSearchTerm, setCurrentSearchTerm] = useState('');

  const resetSearchTerm = useCallback(() => {
    setCurrentSearchTerm('');
    onSearchTermChange('');
  }, []);

  const canSubmit = useMemo(() => {
    const isDirty = selections.some((item) => item.__isDirty);
    const selectionRequirementMet =
      requiredSelections <= 0 ||
      requiredSelections <=
        selections.filter((item) => item.__isSelected).length;
    return isDirty && selectionRequirementMet;
  }, [selections, requiredSelections]);

  const [actionIsInProgress, setActionIsInProgress] = useState(false);

  // When new data comes in, drop any unselected items and merge in the new data.
  useEffect(() => {
    setSelections((selections) =>
      dropUnselectedAndMergeNew(
        selections,
        makeSelectable(data, isItemPreSelected, isItemDisabled),
        accessors.id,
      ),
    );
  }, [data]);

  const updateSelections = useCallback(
    (changed: readonly Selectable<T>[]) => {
      // Fast path optimisation for the whole list changing.
      if (changed.length === selections.length) {
        setSelections(toggleAllDirty(changed));
        return;
      }

      // Overwrite the changed items in the current selections.
      const map = toMap(toggleAllDirty(changed), accessors.id);
      setSelections((selections) =>
        selections.map((datum) => map.get(`${datum[accessors.id]}`) ?? datum),
      );
    },
    [accessors.id],
  );

  const finaliseSelection = useCallback(async () => {
    setActionIsInProgress(true);
    // It's the client's responsibility to handle the promise.
    // We will asborb exceptions and log them to the console.
    onSelect(getSelected(selections))
      .then(() => {
        // Clear the selections so that the next time the modal is opened,
        // it doesn't show the previous selections.
        setSelections(
          dropUnselectedAndMergeNew(
            [],
            makeSelectable(data, isItemPreSelected, isItemDisabled),
            accessors.id,
          ),
        );
        resetSearchTerm();
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        setActionIsInProgress(false);
      });
  }, [onSelect, selections]);

  const cancelSelection = useCallback(() => {
    onCancel();
    //clear the filter
    resetSearchTerm();
    // Clear the selections so that the next time the modal is opened,
    // it doesn't show the previous selections.
    setSelections(
      dropUnselectedAndMergeNew(
        [],
        makeSelectable(data, isItemPreSelected, isItemDisabled),
        accessors.id,
      ),
    );
  }, [onSelect, resetSearchTerm]);

  const { QModalHeader, unmatched } = usePartitionedChildren(
    children,
    'QModalHeader',
  );

  const dataCy = `${dataProps['data-cy'] ?? 'qlookup'}`;

  const actions = useMemo(() => {
    return isReadOnly ? (
      <QButton
        key="lookup-close"
        onClick={onCancel}
        variant="outline"
        data-cy={`${dataCy}-close`}
      >
        Close
      </QButton>
    ) : (
      [
        <QButton
          key="lookup-cancel"
          onClick={cancelSelection}
          variant="outline"
          isDisabled={actionIsInProgress}
          data-cy={`${dataCy}-cancel`}
        >
          Cancel
        </QButton>,
        <QButton
          key="lookup-submit"
          onClick={finaliseSelection}
          isDisabled={!canSubmit}
          isLoading={actionIsInProgress}
          data-cy={`${dataCy}-submit`}
        >
          {action}
        </QButton>,
      ]
    );
  }, [
    isReadOnly,
    canSubmit,
    actionIsInProgress,
    finaliseSelection,
    cancelSelection,
  ]);

  const handleClearAll = useCallback(async () => {
    resetSearchTerm();
    search?.onMustQueryChange(undefined);
    search?.onResourcesChange(search.defaultResources);
  }, [search, resetSearchTerm]);

  return (
    <QModal size={size} isOpen={isOpen} onClose={cancelSelection}>
      {QModalHeader}
      <QModalBody>
        {unmatched}
        <QStack spacing="28px" {...dataProps}>
          {!displayFilters && (
            <QInput
              iconLeftName="Search"
              placeholder={searchPlaceholder}
              onChange={(e) => {
                setCurrentSearchTerm(e.target.value);
                onSearchTermChange(e.target.value);
              }}
              isClearable={true}
              data-cy={`${dataCy}-search-input`}
            />
          )}
          {displayFilters && search && (
            <QFlex gap={2} direction="row">
              <QFlex gap={2} wrap="wrap">
                <QInput
                  maxW="300px"
                  minW="300px"
                  iconLeftName="Search"
                  placeholder={searchPlaceholder}
                  onChange={(e) => {
                    setCurrentSearchTerm(e.target.value);
                    onSearchTermChange(e.target.value);
                  }}
                  isClearable={true}
                  data-cy={`${dataCy}-search-input`}
                  value={currentSearchTerm}
                />
                {Array.isArray(search.defaultResources) && (
                  <QuickSearchFilters
                    companyId={companyId}
                    searchClient={search.client}
                    setMustFilter={search.onMustQueryChange}
                    mustFilter={search.mustQuery}
                    setResources={search.onResourcesChange}
                    resources={search.resource}
                    setResourceFilterSelected={
                      search.onResourceFilterSelectedChange
                    }
                    defaultResources={
                      search.defaultResources as
                        | SearchResource
                        | SearchResource[]
                    }
                  />
                )}
              </QFlex>
              <QBox ml="auto">
                <QButton onClick={handleClearAll} variant="ghost">
                  Clear all
                </QButton>
              </QBox>
            </QFlex>
          )}

          <ConditionalErrorAlert error={error} />
          <div
            style={{
              maxHeight: '60vh',
              overflowY: 'scroll',
            }}
          >
            <QSelectableList<T, Selectable<T>>
              data={selections.filter((item) => !item.__isHidden)}
              onDataChange={updateSelections}
              shouldPin={shouldPin}
              accessors={{
                ...accessors,
                selected: '__isSelected',
                disabled: '__isDisabled',
              }}
              view={view}
              defaultSortBy={defaultSortBy}
              isReadOnly={isReadOnly}
              isDataLoading={isLoading}
              currentSearchTerm={currentSearchTerm}
              filtersSelected={search?.resourceFilterSelected ?? false}
            />
            {hasNextPage && (
              <QFlex justifyContent="center">
                <QButton
                  variant="ghost"
                  onClick={fetchNextPage}
                  isLoading={isLoading || isFetchingNextPage}
                  data-cy={`${dataCy}-load-more`}
                >
                  Load more
                </QButton>
              </QFlex>
            )}
          </div>
        </QStack>
      </QModalBody>
      <QModalActions>{actions}</QModalActions>
    </QModal>
  );
}

/**
 * Converts the data into selectable items by adding a selected key.
 * If a predicate is provided, it will be used to determine if an item is pre-selected.
 * If no predicate is provided, all items will be unselected.
 * If an item is marked as dirty (i.e. its selection state has changed), it will not be
 * checked for pre-selection (but it may still be checked for disabling).
 * @param data - The data to convert. If undefined, an empty array will be used.
 * @param isItemPreselected - A predicate to determine if an item should be pre-selected.
 */
const makeSelectable = <T,>(
  data: readonly T[] | undefined,
  isItemPreselected: DatumPredicate<T> | undefined,
  isItemDisabled: DatumPredicate<T> | undefined,
): Selectable<T>[] =>
  ((data as readonly Selectable<T>[]) ?? []).map((d) => ({
    ...d,
    __isSelected:
      (!d.__isDirty && isItemPreselected?.(d)) ?? d.__isSelected ?? false,
    __isDisabled: isItemDisabled?.(d) ?? d.__isDisabled ?? false,
  }));
